import React, { useState } from "react";
import AnalysisPanel, { PanelEmpty } from "../AnalysisPanel";
import Pagination from "../../shared/Pagination";
import useClientPagination from "../../../hooks/useClientPagination";
import { getCiReportForwardCalendar } from "../../../api/competitiveIntelligence";
import { InferredOccupancy } from "../../../utils/ciFormat";
import ThinScroll from "../../shared/ThinScroll";
import SelectMenu from "../../shared/SelectMenu";
import { PanelTakeaway, fmtDateShort, fmtRate } from "../shared";
import { getDemandSignalTone, DEMAND_SIGNAL_TONE_CLASSES } from "../shared";

const PAGE_SIZE = 15;

const fmtDataAge = (iso) => {
  if (!iso) return "—";
  const timestamp = new Date(iso).getTime();
  if (Number.isNaN(timestamp)) return "—";
  const days = Math.floor((Date.now() - timestamp) / 86400000);
  if (days <= 0) return "today";
  return `${days}d ago`;
};

// >14d old quotes get an amber tint, mirroring the rate calendar's stale rule.
const ageTone = (iso) => {
  if (!iso) return "muted";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days >= 14) return "stale";
  return "fresh";
};

// STATUS blends demand-signal vocabulary (HIGH DEMAND, RESTRICTED, NORMAL) with
// pricing signals (UNDERPRICED, PREMIUM). Demand-derived values route through the
// shared keyword-regex tone classifier so HIGH DEMAND renders red, not emerald.
// UNDERPRICED (red) and PREMIUM (blue) are pricing-specific and keep distinct
// colors since they aren't demand signals.
const PRICING_STATUS_BADGE = {
  UNDERPRICED: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  PREMIUM:     "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
};

const StatusBadge = ({ status }) => {
  if (!status) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] uppercase tracking-wide font-semibold bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">
        —
      </span>
    );
  }
  const pricingCls = PRICING_STATUS_BADGE[status];
  const cls = pricingCls ?? DEMAND_SIGNAL_TONE_CLASSES[getDemandSignalTone(status)].badge;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] uppercase tracking-wide font-semibold ${cls}`}>
      {status}
    </span>
  );
};

const ForwardCalendarBody = ({ rows, totalItems, daysForward, onDaysForwardChange }) => (
  <div>
    <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center gap-3 flex-wrap">
      <span className="inline-flex items-center gap-1.5">
        <span className="text-sm text-gray-600 dark:text-gray-300">Look ahead:</span>
        <SelectMenu
          ariaLabel="Look ahead window"
          value={daysForward}
          onChange={onDaysForwardChange}
          options={[
            { value: 30, label: "30 days" },
            { value: 60, label: "60 days" },
            { value: 90, label: "90 days" },
            { value: 180, label: "180 days" },
          ]}
        />
      </span>
      <span className="text-xs text-gray-500 dark:text-gray-400">
        {totalItems} {totalItems === 1 ? "date" : "dates"}
      </span>
    </div>
    {rows.length === 0 ? (
      <PanelEmpty message="No forward-calendar dates in the selected window." />
    ) : (
      <ThinScroll className="overflow-x-auto responsive-card-table-wrapper">
        <table className="w-full min-w-[1200px] text-sm table-fixed responsive-card-table">
          <colgroup>
            <col style={{ width: "8%" }} />
            <col style={{ width: "16%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "9%" }} />
            <col style={{ width: "9%" }} />
            <col style={{ width: "13%" }} />
            <col style={{ width: "9%" }} />
            <col style={{ width: "8%" }} />
          </colgroup>
          <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
              <th className="px-3 py-2.5 text-left font-semibold">Date</th>
              <th className="px-3 py-2.5 text-left font-semibold">Window</th>
              <th className="px-3 py-2.5 text-right font-semibold">Days Out</th>
              <th className="px-3 py-2.5 text-right font-semibold">Your Rate</th>
              <th className="px-3 py-2.5 text-right font-semibold">Market Avg</th>
              <th className="px-3 py-2.5 text-right font-semibold whitespace-nowrap">Limited Avail.</th>
              <th className="px-3 py-2.5 text-right font-semibold">Min Stay</th>
              <th className="px-3 py-2.5 text-right font-semibold">Comp / Your Occupancy</th>
              <th className="px-3 py-2.5 text-left font-semibold">Status</th>
              <th className="px-3 py-2.5 text-right font-semibold whitespace-nowrap">Data Age</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const compStale = ageTone(row.comp_rate_as_of) === "stale";
              const yourStale = ageTone(row.your_rate_as_of) === "stale";
              return (
                <tr key={`${row.check_in}-${index}`} className="border-b dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition">
                  <td data-label="Date" className="px-3 py-2.5 font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">{fmtDateShort(row.check_in)}</td>
                  <td data-label="Window" className="px-3 py-2.5 text-gray-600 dark:text-gray-300 truncate" title={row.date_window_name || ""}>{row.date_window_name || "—"}</td>
                  <td data-label="Days Out" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{row.days_out ?? "—"}</td>
                  <td data-label="Your Rate" className="px-3 py-2.5 text-right text-gray-900 dark:text-gray-100 font-semibold whitespace-nowrap">{fmtRate(row.your_rate)}</td>
                  <td data-label="Market Avg" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtRate(row.market_avg)}</td>
                  <td data-label="Limited Avail." className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{row.competitors_sold_out ?? 0}</td>
                  <td data-label="Min Stay" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{row.competitors_min_stay ?? 0}</td>
                  <td data-label="Comp / Your Occupancy" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    <span>
                      <InferredOccupancy value={row.comp_occupancy} /> / <InferredOccupancy value={row.your_occupancy} />
                    </span>
                  </td>
                  <td data-label="Status" className="px-3 py-2.5 whitespace-nowrap"><StatusBadge status={row.status} /></td>
                  {/* Stacked ages: "Comp" + "You" labels make the two values self-explanatory
                      and let the column stay narrow without truncating either value. */}
                  <td data-label="Data Age" className="px-3 py-2.5 text-right whitespace-nowrap text-xs leading-tight">
                    <div>
                      <div
                        className={compStale ? "text-amber-600 dark:text-amber-400" : "text-gray-500 dark:text-gray-400"}
                        title={row.comp_rate_as_of || ""}
                      >
                        <span className="text-[10px] uppercase tracking-wide opacity-70 mr-1">Comp</span>
                        {fmtDataAge(row.comp_rate_as_of)}
                      </div>
                      <div
                        className={yourStale ? "text-amber-600 dark:text-amber-400" : "text-gray-500 dark:text-gray-400"}
                        title={row.your_rate_as_of || ""}
                      >
                        <span className="text-[10px] uppercase tracking-wide opacity-70 mr-1">You</span>
                        {fmtDataAge(row.your_rate_as_of)}
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </ThinScroll>
    )}
  </div>
);

const ForwardCalendarBodyPaginated = ({ rows, daysForward, onDaysForwardChange }) => {
  const { page, setPage, pageItems, totalPages, totalItems } = useClientPagination(rows, { pageSize: PAGE_SIZE });
  return (
    <>
      <ForwardCalendarBody
        rows={pageItems}
        totalItems={totalItems}
        daysForward={daysForward}
        onDaysForwardChange={onDaysForwardChange}
      />
      {totalPages > 1 && (
        <div className="px-5 pt-1 pb-3 border-t border-gray-200 dark:border-gray-800">
          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            totalItems={totalItems}
            perPage={PAGE_SIZE}
            label="dates"
          />
        </div>
      )}
    </>
  );
};

const ForwardCalendarPanel = ({ clientId, categoryCode, say }) => {
  const [daysForward, setDaysForward] = useState(90);

  return (
    <AnalysisPanel
      id={`forward-calendar-${clientId}-${categoryCode || "all"}-${daysForward}`}
      title="Forward Calendar"
      tip="Master per-date table — rate, occupancy, competitor signals, and a one-word STATUS (UNDERPRICED / PREMIUM / HIGH DEMAND / RESTRICTED / NORMAL). The widest-angle view of what's happening over the next 90 days."
      description="Comprehensive per-date view: rate, occupancy, limited-availability competitors, status. Includes rate-freshness timestamps."
      badge={categoryCode ? categoryCode.toUpperCase() : null}
      fetcher={() => getCiReportForwardCalendar(clientId, { categoryCode: categoryCode || "rv", daysForward })}
      deps={[clientId, categoryCode, daysForward]}
    >
      {({ data }) => {
        const rows = Array.isArray(data) ? data : (data?.items ?? []);
        return (
          <>
            <PanelTakeaway panelKey="forward_calendar" say={say} />
            <ForwardCalendarBodyPaginated
              rows={rows}
              daysForward={daysForward}
              onDaysForwardChange={setDaysForward}
            />
          </>
        );
      }}
    </AnalysisPanel>
  );
};

export default ForwardCalendarPanel;
